import type { ChatMessage } from '@teo/widget-shared';
import {
  buildOptimisticAttachmentFromText,
  normalizeMediaUrl,
  optimisticTextForOutgoingSend,
  sortMessagesByDate,
} from '@teo/widget-shared';
import { isTypingPlaceholderMessage } from './typing-placeholder.js';
import {
  bindServerIdToClientKey,
  getClientIdBoundToServerId,
  linkClientIdToImageUrl,
  resolveImageDisplayUrl,
} from './image-display-cache.js';
import { logImage, shortImageUrl, summarizeImageMessage } from './image-logger.js';

const MATCH_WINDOW_MS = 120_000;

function messageMediaUrl(message: ChatMessage): string | undefined {
  return message.mediaUrl?.trim() || message.attachment?.url?.trim() || undefined;
}

/** Canonical URL for dedupe when server stores link in `attachment` and client in `text`. */
function canonicalUserSendUrl(message: ChatMessage): string | undefined {
  const fromMedia = messageMediaUrl(message);
  if (fromMedia) return normalizeMediaUrl(fromMedia);

  const text = message.text?.trim();
  if (text && /^https?:\/\//i.test(text)) {
    return normalizeMediaUrl(text);
  }

  return undefined;
}

function userSendUrlsMatch(a: ChatMessage, b: ChatMessage): boolean {
  const urlA = canonicalUserSendUrl(a);
  const urlB = canonicalUserSendUrl(b);
  return Boolean(urlA && urlB && urlA === urlB);
}

/** Whether two user rows describe the same outbound send (for dedupe / reconcile). */
export function areSameUserSend(a: ChatMessage, b: ChatMessage): boolean {
  if (a.role !== 'user' || b.role !== 'user') return false;

  if (a.clientId && (b.clientId === a.clientId || b.id === a.clientId)) {
    return true;
  }
  if (b.clientId && (a.clientId === b.clientId || a.id === b.clientId)) {
    return true;
  }

  const timeDiff = Math.abs(
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  if (timeDiff >= MATCH_WINDOW_MS) return false;

  if (userSendUrlsMatch(a, b)) return true;

  const typeA = a.contentType ?? 'text';
  const typeB = b.contentType ?? 'text';
  if (typeA !== typeB) return false;

  if (typeA === 'image') {
    const urlA = messageMediaUrl(a);
    const urlB = messageMediaUrl(b);
    if (urlA && urlB && urlA === urlB) return true;
    return false;
  }

  const textA = a.text.trim();
  const textB = b.text.trim();
  if (!textA || !textB) return false;

  return textA === textB;
}

export function getMessageKey(message: ChatMessage): string {
  return message.clientId ?? message.id;
}

function createdAtDistanceMs(a: ChatMessage, b: ChatMessage): number {
  return Math.abs(
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}

function pickClosestServerMatch(
  pending: ChatMessage,
  matches: ChatMessage[],
): ChatMessage | undefined {
  if (matches.length === 0) return undefined;
  if (matches.length === 1) return matches[0];
  return matches.reduce((best, current) =>
    createdAtDistanceMs(current, pending) < createdAtDistanceMs(best, pending)
      ? current
      : best,
  );
}

/** Find server message that confirms a pending optimistic send. */
export function findServerMatchForPending(
  pending: ChatMessage,
  serverMessages: ChatMessage[],
): ChatMessage | undefined {
  if (pending.clientId) {
    const byClientId = serverMessages.find(
      (m) => m.clientId === pending.clientId || m.id === pending.clientId,
    );
    if (byClientId) return byClientId;
  }

  const pendingTime = new Date(pending.createdAt).getTime();

  if (pending.contentType === 'image') {
    const pendingUrl = messageMediaUrl(pending);
    const matches = serverMessages.filter((m) => {
      if (m.role !== 'user' || m.contentType !== 'image') return false;
      if (
        Math.abs(new Date(m.createdAt).getTime() - pendingTime) >=
        MATCH_WINDOW_MS
      ) {
        return false;
      }
      const serverUrl = messageMediaUrl(m);
      return Boolean(pendingUrl && serverUrl && pendingUrl === serverUrl);
    });
    return pickClosestServerMatch(pending, matches);
  }

  const pendingUrl = canonicalUserSendUrl(pending);
  if (pendingUrl) {
    const matches = serverMessages.filter((m) => {
      if (m.role !== 'user') return false;
      if (
        Math.abs(new Date(m.createdAt).getTime() - pendingTime) >=
        MATCH_WINDOW_MS
      ) {
        return false;
      }
      if (userSendUrlsMatch(pending, m)) return true;
      return m.text === pending.text;
    });
    return pickClosestServerMatch(pending, matches);
  }

  const matches = serverMessages.filter(
    (m) =>
      m.role === 'user' &&
      m.text === pending.text &&
      Math.abs(new Date(m.createdAt).getTime() - pendingTime) < MATCH_WINDOW_MS,
  );
  return pickClosestServerMatch(pending, matches);
}

export function isPendingUserMessage(message: ChatMessage): boolean {
  return (
    message.role === 'user' &&
    (message.status === 'sending' || message.status === 'failed')
  );
}

/** Whether a local user row is already represented in the server snapshot. */
function isLocalAlreadyOnServer(
  local: ChatMessage,
  serverMessages: ChatMessage[],
): boolean {
  if (
    serverMessages.some(
      (server) =>
        String(server.id) === String(local.id) ||
        (local.clientId != null &&
          (server.clientId === local.clientId ||
            String(server.id) === local.clientId)),
    )
  ) {
    return true;
  }

  return Boolean(findServerMatchForPending(local, serverMessages));
}

export function createOptimisticUserMessage(
  text: string,
  clientId: string,
  createdAt?: string,
): ChatMessage {
  const now = createdAt ?? new Date().toISOString();
  const attachment = buildOptimisticAttachmentFromText(text);
  return {
    id: clientId,
    clientId,
    role: 'user',
    text: optimisticTextForOutgoingSend(text, attachment),
    createdAt: now,
    status: 'sending',
    contentType: 'text',
    attachment,
  };
}

export function createOptimisticImageMessage(
  previewUrl: string,
  clientId: string,
  fileName?: string,
  createdAt?: string,
): ChatMessage {
  const now = createdAt ?? new Date().toISOString();
  return {
    id: clientId,
    clientId,
    role: 'user',
    text: '',
    createdAt: now,
    status: 'sending',
    contentType: 'image',
    mediaUrl: previewUrl,
    attachment: {
      kind: 'image',
      url: previewUrl,
      fileName,
    },
  };
}

export function addOptimisticImageMessage(
  messages: ChatMessage[],
  previewUrl: string,
  clientId: string,
  fileName?: string,
  createdAt?: string,
): ChatMessage[] {
  return sortMessagesByDate([
    ...messages,
    createOptimisticImageMessage(previewUrl, clientId, fileName, createdAt),
  ]);
}

export function addOptimisticMessage(
  messages: ChatMessage[],
  text: string,
  clientId: string,
  createdAt?: string,
): ChatMessage[] {
  return sortMessagesByDate([
    ...messages,
    createOptimisticUserMessage(text, clientId, createdAt),
  ]);
}

export function patchOptimisticImageUrl(
  messages: ChatMessage[],
  clientId: string,
  uploadedUrl: string,
  fileName?: string,
): ChatMessage[] {
  return messages.map((message) => {
    if (message.clientId !== clientId && message.id !== clientId) {
      return message;
    }
    const next = {
      ...message,
      mediaUrl: uploadedUrl,
      attachment: {
        kind: 'image' as const,
        url: uploadedUrl,
        fileName: fileName ?? message.attachment?.fileName,
      },
    };
    logImage('store', 'patchOptimisticImageUrl', {
      clientId,
      from: shortImageUrl(message.mediaUrl ?? message.attachment?.url),
      to: shortImageUrl(uploadedUrl),
    });
    return next;
  });
}

export function confirmOptimisticMessage(
  messages: ChatMessage[],
  clientId: string,
  serverMessage: ChatMessage,
): ChatMessage[] {
  const isImage = serverMessage.contentType === 'image';

  const existing =
    messages.find(
      (message) => message.clientId === clientId || message.id === clientId,
    ) ??
    messages.find((message) =>
      areSameUserSend(
        { ...serverMessage, clientId, role: 'user' as const },
        message,
      ),
    );

  const serverMediaUrl = messageMediaUrl(serverMessage);
  const isImageMessage =
    isImage || existing?.contentType === 'image';

  const confirmed: ChatMessage = {
    ...serverMessage,
    clientId,
    status: 'sent' as const,
    createdAt: existing?.createdAt ?? serverMessage.createdAt,
  };

  const existingUrl = existing ? canonicalUserSendUrl(existing) : undefined;
  const confirmedUrl = canonicalUserSendUrl(serverMessage);
  if (existingUrl && confirmedUrl && existingUrl === confirmedUrl) {
    confirmed.attachment = serverMessage.attachment ?? existing?.attachment;
    confirmed.text = serverMessage.text?.trim()
      ? serverMessage.text
      : optimisticTextForOutgoingSend(
          existing?.text ?? serverMessage.text,
          confirmed.attachment,
        );
    confirmed.createdAt = existing?.createdAt ?? confirmed.createdAt;
  }

  if (isImageMessage) {
    const stableUrl =
      serverMediaUrl ??
      (existing ? messageMediaUrl(existing) : undefined) ??
      serverMessage.mediaUrl;
    confirmed.mediaUrl = stableUrl;
    confirmed.attachment =
      serverMessage.attachment ??
      (stableUrl
        ? {
            kind: 'image' as const,
            url: stableUrl,
            fileName: existing?.attachment?.fileName,
          }
        : existing?.attachment);

    if (clientId && confirmed.id && String(confirmed.id) !== clientId) {
      bindServerIdToClientKey(clientId, String(confirmed.id), stableUrl);
    }
  }

  logImage('store', 'confirmOptimisticMessage', {
    clientId,
    before: existing ? summarizeImageMessage(existing) : undefined,
    after: summarizeImageMessage(confirmed),
  });

  let replaced = false;

  const next = messages.map((message) => {
    if (message.clientId !== clientId && message.id !== clientId) {
      return message;
    }
    replaced = true;
    return confirmed;
  });

  if (replaced) {
    return sortMessagesByDate(next);
  }

  // WS refetch may have swapped the optimistic row for the server message first.
  const existingOnServer = findServerMatchForPending(confirmed, messages);
  if (existingOnServer) {
    return sortMessagesByDate(
      messages.map((message) =>
        message.id === existingOnServer.id ? confirmed : message,
      ),
    );
  }

  if (messages.some((message) => message.id === serverMessage.id)) {
    return sortMessagesByDate(
      messages.map((message) =>
        message.id === serverMessage.id ? confirmed : message,
      ),
    );
  }

  return sortMessagesByDate([...messages, confirmed]);
}

export function markMessageFailed(
  messages: ChatMessage[],
  clientId: string,
  sendError?: ChatMessage['sendError'],
): ChatMessage[] {
  return messages.map((message) =>
    message.clientId === clientId || message.id === clientId
      ? {
          ...message,
          status: 'failed' as const,
          sendError: sendError ?? message.sendError ?? 'generic',
        }
      : message,
  );
}

export function markMessageSending(
  messages: ChatMessage[],
  clientId: string,
): ChatMessage[] {
  return messages.map((message) => {
    if (message.clientId !== clientId && message.id !== clientId) {
      return message;
    }
    const { sendError, ...rest } = message;
    void sendError;
    return { ...rest, status: 'sending' as const };
  });
}

/**
 * Merge server history with in-flight / failed local messages.
 * Server wins when a pending message is already confirmed on backend.
 */
export function reconcileMessages(
  serverMessages: ChatMessage[],
  localMessages: ChatMessage[],
): ChatMessage[] {
  const locals = localMessages.filter(
    (message) => !isTypingPlaceholderMessage(message),
  );
  const pending = locals.filter(isPendingUserMessage);
  const merged = [...serverMessages];

  for (const local of pending) {
    if (local.status === 'sending') {
      const matched = findServerMatchForPending(local, serverMessages);
      if (!matched) merged.push(local);
      continue;
    }

    if (local.status === 'failed') {
      const matched = findServerMatchForPending(local, serverMessages);
      if (!matched) merged.push(local);
    }
  }

  for (const local of locals) {
    if (local.role !== 'user' || local.status !== 'sent') continue;
    if (isLocalAlreadyOnServer(local, serverMessages)) continue;
    merged.push(local);
  }

  return sortMessagesByDate(
    preserveUserMessageIdentity(
      dedupeMatchedUserMessages(sortMessagesByDate(merged)),
      locals,
    ),
  );
}

function pickBestSameSendLocal(
  serverMessage: ChatMessage,
  candidates: ChatMessage[],
): ChatMessage | undefined {
  const sameSend = candidates.filter((local) =>
    areSameUserSend(local, serverMessage),
  );
  if (sameSend.length === 0) return undefined;
  if (sameSend.length === 1) return sameSend[0];

  // Repeat sends of the same URL: bind by closest createdAt, not first match.
  const ranked = [...sameSend].sort(
    (a, b) =>
      createdAtDistanceMs(a, serverMessage) -
      createdAtDistanceMs(b, serverMessage),
  );
  const closestDist = createdAtDistanceMs(ranked[0]!, serverMessage);
  const tied = ranked.filter(
    (local) => createdAtDistanceMs(local, serverMessage) === closestDist,
  );
  if (tied.length > 1) {
    return tied.find(isOptimisticClientRow) ?? tied[0];
  }
  return ranked[0];
}

function findLocalUserMessage(
  message: ChatMessage,
  localMessages: ChatMessage[],
): ChatMessage | undefined {
  const candidates = localMessages.filter((local) => local.role === 'user');

  const boundClientId = getClientIdBoundToServerId(String(message.id));
  if (boundClientId) {
    const byBound = candidates.find(
      (local) => local.clientId === boundClientId,
    );
    if (byBound) return byBound;
  }

  // 1. Direct clientId / id link — only when the server row actually carries a
  //    clientId (never match `undefined === undefined`, which would grab a
  //    random history row and drop the optimistic clientId → key remount).
  if (message.clientId != null) {
    const linked = candidates.find(
      (local) =>
        local.clientId === message.clientId || local.id === message.clientId,
    );
    if (linked) return linked;
  }

  const byId = candidates.find(
    (local) => String(local.id) === String(message.id),
  );
  if (byId) return byId;

  return pickBestSameSendLocal(message, candidates);
}

/** Keep clientId + stable display URL after HTTP/WS reconcile (prevents img remount). */
function preserveUserMessageIdentity(
  messages: ChatMessage[],
  localMessages: ChatMessage[],
): ChatMessage[] {
  return messages.map((message) => {
    if (message.role !== 'user') return message;

    const local = findLocalUserMessage(message, localMessages);
    const clientId = local?.clientId ?? message.clientId;

    if (message.contentType !== 'image') {
      if (!local) {
        if (!clientId || message.clientId) return message;
        return { ...message, clientId };
      }
      const resolvedClientId = local.clientId ?? message.clientId ?? clientId;
      // Warm the id→clientId cache so resolveMessageReactKey stays stable even
      // if a later snapshot drops clientId from this row.
      if (resolvedClientId && message.id && String(message.id) !== resolvedClientId) {
        bindServerIdToClientKey(resolvedClientId, String(message.id));
      }
      return {
        ...message,
        clientId: resolvedClientId,
        createdAt: local.clientId ? local.createdAt : message.createdAt,
      };
    }

    const serverUrl = messageMediaUrl(message);
    const localUrl = local ? messageMediaUrl(local) : undefined;

    if (clientId && message.id && String(message.id) !== clientId) {
      bindServerIdToClientKey(
        clientId,
        String(message.id),
        serverUrl ?? localUrl,
      );
    }

    const displayUrl = resolveImageDisplayUrl(
      clientId,
      message.id,
      serverUrl ?? localUrl,
    );

    if (!clientId && !displayUrl) return message;

    const url = displayUrl ?? serverUrl ?? localUrl;
    const next: ChatMessage = {
      ...message,
      clientId,
      mediaUrl: url,
      attachment: url
        ? {
            kind: 'image',
            url,
            fileName:
              message.attachment?.fileName ?? local?.attachment?.fileName,
          }
        : message.attachment,
    };

    if (clientId && url) {
      linkClientIdToImageUrl(clientId, url);
    }

    if (
      local &&
      (local.clientId !== message.clientId ||
        shortImageUrl(messageMediaUrl(message)) !== shortImageUrl(url))
    ) {
      logImage('store', 'reconcile preserved image identity', {
        id: message.id,
        clientId,
        from: summarizeImageMessage(message),
        to: summarizeImageMessage(next),
      });
    }

    return next;
  });
}

/** Drop duplicate user rows (optimistic clientId + server id for same send). */
function isLinkedClientServerPair(a: ChatMessage, b: ChatMessage): boolean {
  if (a.clientId && String(b.id) === a.clientId) return true;
  if (b.clientId && String(a.id) === b.clientId) return true;
  if (a.clientId && a.clientId === b.clientId) return true;
  return false;
}

/** Only collapse optimistic/local ↔ server pairs — never two server history rows. */
function shouldDedupeUserPair(a: ChatMessage, b: ChatMessage): boolean {
  if (!areSameUserSend(a, b)) return false;
  if (isOptimisticClientRow(a) || isOptimisticClientRow(b)) return true;
  if (isLinkedClientServerPair(a, b)) return true;
  return false;
}

function dedupeMatchedUserMessages(messages: ChatMessage[]): ChatMessage[] {
  const result: ChatMessage[] = [];

  for (const message of messages) {
    if (message.role !== 'user') {
      result.push(message);
      continue;
    }

    const duplicateIndex = result.findIndex((existing) =>
      shouldDedupeUserPair(existing, message),
    );

    if (duplicateIndex < 0) {
      result.push(message);
      continue;
    }

    const existing = result[duplicateIndex]!;
    const preferMessage = pickPreferredUserDuplicate(existing, message);
    result[duplicateIndex] = {
      ...preferMessage,
      clientId: existing.clientId ?? message.clientId,
      createdAt: resolveOptimisticCreatedAt(existing, message, preferMessage),
      status: 'sent' as const,
    };
  }

  return result;
}

function isOptimisticClientRow(message: ChatMessage): boolean {
  return Boolean(message.clientId && message.id === message.clientId);
}

function resolveOptimisticCreatedAt(
  a: ChatMessage,
  b: ChatMessage,
  preferred: ChatMessage,
): string {
  if (isOptimisticClientRow(a)) return a.createdAt;
  if (isOptimisticClientRow(b)) return b.createdAt;
  return preferred.createdAt;
}

function pickPreferredUserDuplicate(
  a: ChatMessage,
  b: ChatMessage,
): ChatMessage {
  const aIsClientId = a.clientId != null && a.id === a.clientId;
  const bIsClientId = b.clientId != null && b.id === b.clientId;

  if (aIsClientId && !bIsClientId) return b;
  if (bIsClientId && !aIsClientId) return a;

  return Number(b.id) > Number(a.id) ? b : a;
}

export function findMessageByClientId(
  messages: ChatMessage[],
  clientId: string,
): ChatMessage | undefined {
  return messages.find(
    (m) => m.clientId === clientId || m.id === clientId,
  );
}
